/// <reference types="jquery.colorbox" />
/// <reference types="@rspack/core/module" />

import type { Faro } from "@grafana/faro-react";
import * as Sentry from "@sentry/react";
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import type { IConfig as UnleashConfig } from "@unleash/proxy-client-react";
import Pusher from "pusher-js";
import * as React from "react";
import {
  type NavigateOptions,
  type To,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { browserExtensionUrlPatterns } from "@/shared/bootstrap/browserExtensionErrors.ts";
import { prefetchCurrentUser } from "@/shared/queries/CurrentUser.tsx";
import { prefetchInterfaceSettings } from "@/shared/queries/InterfaceSetting.tsx";
import { prefetchLoggedInStatus } from "@/shared/queries/loggedInStatus.ts";
import type { CVPartnerReactRoute, Options, Router } from "@/shared/types.ts";
import { getTokenHeaders, httpChecked } from "@/shared/util/fetch.tsx";
import { skipFetchingUser } from "@/shared/util/getCvPartner.ts";
import { invariant } from "@/shared/util/invariant.ts";
import { Subscriber } from "@/shared/util/subscriber.ts";
import {
  I18n as I18nObject,
  initTranslations,
  toLocaleTag,
} from "@/shared/util/translations.ts";
import {
  type SentryConfig,
  bootstrapChecker,
  getBootstrap,
  getDeployConfig,
  readSentryConfig,
  setCsrfToken,
} from "./bootContract.ts";
import {
  enforceBootRedirect,
  enforceInternalUser,
  restoreReturnTo,
} from "./bootRedirect.ts";
import { createFaro } from "./metrics.ts";
import { renderBootError, renderRouter } from "./react.tsx";

import "jquery";
import "jquery-colorbox";

declare global {
  var I18n: typeof I18nObject;
}

globalThis.I18n = I18nObject;

type ReactRoute = new (options: Options) => CVPartnerReactRoute;

interface PackOptions {
  requireInternalUser?: boolean;
}

const UNLEASH_APP_NAME = "cvpartner-js";
const UNLEASH_PATH = "/unleash/api/frontend";

export class Cvpartner {
  faro?: Faro;
  subscriber?: Subscriber;
  unleashConfig?: UnleashConfig;
  error?: unknown;
  private router: Router | null = null;
  isLoggedIn = false;
  canFetchCurrentUser = false;
  aws_region = "eu-west-1";
  // Until boot reads the real one, claim the highest possible version:
  // useClientOutdated compares this against the server's current minimum, and a
  // zero here asks every viewer to reload a page a reload cannot fix.
  minimumCompatibleVersion = Number.MAX_SAFE_INTEGER;
  disableTimeoutAndCompatibilityChecks = false;

  constructor(
    private readonly reactRoute: ReactRoute,
    private readonly packOptions: PackOptions = {},
  ) {}

  init() {
    try {
      this.boot();
    } catch (error) {
      Sentry.captureException(error);
      renderBootError();

      throw error;
    }
  }

  private initSentry({ sentryDsn, environment, release }: SentryConfig) {
    if (!sentryDsn || sentryDsn.trim() === "") {
      return;
    }

    Sentry.init({
      dsn: sentryDsn,
      environment: environment || "unknown",
      release: release || "unknown",
      denyUrls: browserExtensionUrlPatterns,
      beforeSend:
        process.env.NODE_ENV === "development"
          ? (event, hint) => {
              const message =
                hint.originalException ??
                hint.syntheticException ??
                event.message;

              if (event.level === "fatal" || event.level === "error") {
                console.error(message);
              } else {
                console.log(message);
              }

              return event;
            }
          : undefined,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
    });

    // This is useful for debugging
    (window as any).Sentry = Sentry;
  }

  private boot() {
    // Sentry first: the deploy config carries its configuration, so a mismatch
    // reported while validating would otherwise go to a client that does not
    // exist yet.
    const sentryConfig = readSentryConfig();

    if (sentryConfig.environment !== "test") {
      this.initSentry(sentryConfig);
    }

    const bootstrap = getBootstrap();
    const deployConfig = getDeployConfig();

    document.documentElement.lang = toLocaleTag(bootstrap.locale);
    initTranslations({
      locale: bootstrap.locale,
      defaultLocale: bootstrap.default_locale,
    });

    if (enforceBootRedirect(bootstrap)) {
      // Navigation is in flight; render nothing.
      return;
    }

    if (restoreReturnTo(bootstrap)) {
      // Navigation is in flight; render nothing.
      return;
    }

    if (
      this.packOptions.requireInternalUser &&
      enforceInternalUser(bootstrap)
    ) {
      return;
    }

    const options: Options = {
      pusher_key: bootstrap.pusher_key,
      pusher_cluster: bootstrap.pusher_cluster,
      current_user_id: bootstrap.current_user_id,
      current_user_roles: bootstrap.current_user_roles,
      aws_region: deployConfig.awsRegion,
      environment: deployConfig.environment,
      release: deployConfig.release,
    };

    this.isLoggedIn = options.current_user_id != null;
    this.canFetchCurrentUser = this.isLoggedIn && !skipFetchingUser();
    this.minimumCompatibleVersion = bootstrap.minimum_compatible_version;
    this.disableTimeoutAndCompatibilityChecks =
      bootstrap.disable_timeout_and_compatibility_checks;

    if (this.canFetchCurrentUser) {
      void Promise.all([prefetchCurrentUser(), prefetchInterfaceSettings()]);
    } else {
      void prefetchLoggedInStatus();
    }

    if (options.environment === "test") {
      this.subscriber = new Subscriber();
    } else {
      try {
        this.faro = createFaro(options);
        const clientKey = deployConfig.unleashClientKey;

        if (clientKey) {
          const { userId, properties } = bootstrap.unleash_context;

          this.unleashConfig = {
            appName: UNLEASH_APP_NAME,
            url: new URL(UNLEASH_PATH, window.location.origin).href,
            clientKey,
            environment: deployConfig.environment,
            context: {
              userId,
              properties: {
                ...properties,
                // unleash types every property as a string, and this one is a
                // number. Both serialise the same into the request.
                companyCreatedAt: String(properties.companyCreatedAt),
              },
            },
          };
        }
        const headers = getTokenHeaders();
        const pusher = new Pusher(options.pusher_key, {
          auth: { headers },
          cluster: options.pusher_cluster,
        });
        this.subscriber = new Subscriber(pusher);
      } catch (error) {
        this.error = error;
        this.subscriber = new Subscriber();
      }

      if (this.error) {
        Sentry.captureException(this.error);
        this.faro?.api.pushError(this.error as Error);
      }
    }

    if (options.aws_region) {
      this.aws_region = options.aws_region;
    }

    const route = new this.reactRoute(options);

    const { router, selector } = route.render();

    renderRouter(router, selector, toLocaleTag(bootstrap.locale));

    this.router = router;
  }

  navigate(to: number): Promise<void>;
  navigate(to: To | null, opts?: NavigateOptions): Promise<void>;
  async navigate(to: number | To | null, opts?: NavigateOptions) {
    const { router } = this;

    invariant(router, "Router is not initialized");

    if (typeof to === "number") {
      await router.navigate(to);
    } else {
      await router.navigate(to, opts);
    }
  }

  subscribeToRouter(callback: Parameters<Router["subscribe"]>[0]) {
    const { router } = this;

    invariant(router, "Router is not initialized");

    return router.subscribe(callback);
  }
}

const bootstrapQuery = queryOptions({
  queryKey: ["bootstrap"] as const,
  queryFn: ({ signal }) =>
    httpChecked.get("/bootstrap", bootstrapChecker, {
      signal,
      skipLoadingIndicator: true,
    }),
});

export async function refreshCsrfToken(queryClient: QueryClient) {
  try {
    const bootstrap = await queryClient.query(bootstrapQuery);

    setCsrfToken(bootstrap.csrf_token);
  } catch {
    // empty
  }
}

export function setCvPartner(instance: Cvpartner) {
  // @ts-expect-error - server-rendered pages expect this to be a global
  window.cvpartner = instance;
}

// Whenever we start using html history instead of hash history, this needs to
// be removed.
const { location } = window;
if (location.hash[0] === "#" && location.hash[1] !== "/") {
  location.hash = `#/${location.hash.slice(1)}`;
}
